import React from 'react'
import tw from 'tailwind-styled-components'
import {Dropdown, DropdownProps} from '@components/common/dropdown/dropdown'
import {cn} from '@containers/html-utils'
import {Icon} from '../base/icon'
import {TablerIcons} from '../icon-tabler/icon-tabler'
import ArrowDownIcon from './arrow-down.svg'
import ArrowDownSmallIcon from './arrow-down-small.svg'

const TwText = tw.div`
    whitespace-nowrap
    overflow-hidden
    text-ellipsis
    tracking-tighter
`

const LinkButton = tw.div<{$open?: boolean}>`
    inline-flex
    items-center
    justify-between
    select-none
    whitespace-nowrap
    cursor-pointer
    text-action-default
    hover:text-action-hover
    active:text-action-pressed
    text-12
    h-16
`

const Button = tw.div<{$open?: boolean}>`
    inline-flex
    items-center
    justify-between
    select-none
    whitespace-nowrap
    text-primary
    rounded-8
    border
    border-solid
    py-8
    px-12
    ${p => (p.$open ? `border-2 !py-7 !px-11` : '')}
    focus:border-action-default
    outline-none
`

export interface DropdownButtonProps {
  content: React.ReactNode
  open: boolean
  onOpenChange: (open: boolean) => void
  link?: boolean
  className?: string
  disabled?: boolean
  children: React.ReactNode
  large?: boolean
  dataTestId?: string
  onlyText?: boolean
  textClassName?: string
  icon?: TablerIcons
  iconClassName?: string
  placement?: DropdownProps['placement']
  hideArrow?: boolean
}

export const DropdownIcon = (props: DropdownButtonProps) => {
  return (
    <Dropdown
      onVisibleChange={() => props.onOpenChange(false)}
      visible={props.open}
      duration={0}
      content={props.content}
    >
      <div
        className={cn(
          'hover:text-action-hover inline-flex',
          props.open ? 'text-third-old' : 'text-obscure-old',
          props.className
        )}
        onClick={e => {
          e.stopPropagation()
          if (props.disabled) {
            return
          }
          props.onOpenChange(true)
        }}
      >
        {props.children}
      </div>
    </Dropdown>
  )
}

export const DropdownButton = (props: DropdownButtonProps) => {
  const ButtonComponent = props.link ? LinkButton : Button

  let Content = undefined

  if (props.onlyText) {
    Content = (
      <div
        className="flex items-center w-fit cursor-pointer"
        onClick={() => {
          if (props.disabled) {
            return
          }
          props.onOpenChange(true)
        }}
      >
        {props.icon && (
          <Icon icon={props.icon} className={cn(props.iconClassName, 'mr-4')} />
        )}
        <TwText className={props.textClassName}>{props.children}</TwText>
      </div>
    )
  } else {
    Content = (
      <ButtonComponent
        className={cn(
          props.large ? 'h-32 text-14' : 'h-24 text-12',
          !props.link &&
            (props.disabled
              ? 'bg-input-disabled-old'
              : 'bg-input-old cursor-pointer'),
          !props.link &&
            (props.open ? 'border-action-default' : 'border-border-base'),
          props.className
        )}
        $open={props.open}
        onClick={() => {
          if (props.disabled) {
            return
          }
          props.onOpenChange(true)
        }}
        data-test-id={props.dataTestId}
      >
        <TwText className={props.textClassName}>{props.children}</TwText>
        {props.link ? (
          <ArrowDownSmallIcon className="shrink-0" />
        ) : (
          <ArrowDownIcon
            className={cn(
              'shrink-0',
              props.open
                ? 'text-action-default rotate-180'
                : 'text-icon-secondary'
            )}
          />
        )}
      </ButtonComponent>
    )
  }

  return (
    <Dropdown
      hideArrow={props.hideArrow ?? true}
      onVisibleChange={() => props.onOpenChange(false)}
      visible={props.open}
      placement={props.placement}
      duration={0}
      content={props.content}
    >
      {Content}
    </Dropdown>
  )
}
